import React, { useMemo, useState } from 'react';
import { DownloadIcon, PlusIcon, PrinterIcon, SearchIcon, Trash2Icon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { useData } from '../../store/DataContext';
import { SalesReceipt, SalesReceiptItem } from '../../types';
import { calculateSalesReceiptTotals, formatCurrency } from './utils';
type SalesReceiptFormProps = {
  onCancel: () => void;
  onSaved: (receipt: SalesReceipt, action: SalesReceiptOutputAction) => void;
};
type SalesReceiptOutputAction = 'print' | 'download';
type EditableField = 'itemDescription' | 'quantity' | 'rate';
export function SalesReceiptForm({
  onCancel,
  onSaved
}: SalesReceiptFormProps) {
  const {
    billingItems,
    currentUser,
    getNextSalesReceiptNumber,
    createSalesReceipt,
    users
  } = useData();
  const [billTo, setBillTo] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [visitDate, setVisitDate] = useState(new Date().toISOString().slice(0, 10));
  const [salespersonId, setSalespersonId] = useState(currentUser?.id || '');
  const [items, setItems] = useState<SalesReceiptItem[]>([]);
  const [showItemSearch, setShowItemSearch] = useState(false);
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const activeBillingItems = billingItems.filter(item => item.status === 'Active');
  const filteredItems = activeBillingItems.filter(item => {
    const query = itemSearchQuery.trim().toLowerCase();
    return !query || item.serviceName.toLowerCase().includes(query) || item.itemCode.toLowerCase().includes(query);
  });
  const totals = useMemo(() => calculateSalesReceiptTotals(items), [items]);
  const addCatalogueItem = (item: (typeof billingItems)[number]) => {
    setItems(current => [...current, createSalesReceiptItem({
      itemId: item.id,
      itemDescription: item.description ? `${item.serviceName} — ${item.description}` : item.serviceName,
      rate: item.unitPrice
    })]);
    setItemSearchQuery('');
    setShowItemSearch(false);
  };
  const addManualItem = () => {
    setItems(current => [...current, createSalesReceiptItem({
      itemDescription: '',
      rate: 0
    })]);
  };
  const updateItem = (itemId: string, field: EditableField, value: string | number) => {
    setItems(current => current.map(item => {
      if (item.id !== itemId) return item;
      const next = {
        ...item,
        [field]: field === 'itemDescription' ? String(value) : Number(value)
      };
      return {
        ...next,
        quantity: Math.max(1, finiteNumber(next.quantity)),
        rate: Math.max(0, finiteNumber(next.rate)),
        amount: Math.max(1, finiteNumber(next.quantity)) * Math.max(0, finiteNumber(next.rate))
      };
    }));
  };
  const saveReceipt = async (action: SalesReceiptOutputAction) => {
    if (isSaving) return;
    const trimmedBillTo = billTo.trim();
    const trimmedCustomerPhone = customerPhone.trim();
    const selectedSalesperson = users.find(user => user.id === salespersonId);
    if (!trimmedBillTo) {
      setError('Enter the customer or organization to bill.');
      return;
    }
    if (!visitDate) {
      setError('Select the visit date for this cash sale.');
      return;
    }
    if (!selectedSalesperson) {
      setError('Select the sales person who completed this cash sale.');
      return;
    }
    if (!items.length) {
      setError('Add at least one item to the sales receipt.');
      return;
    }
    if (items.some(item => !item.itemDescription.trim())) {
      setError('Enter an item and description for every receipt line.');
      return;
    }
    if (items.some(item => item.quantity <= 0 || item.rate < 0)) {
      setError('Each line needs a valid quantity and rate.');
      return;
    }
    setError(null);
    setIsSaving(true);
    const receipt = await createSalesReceipt({
      billTo: trimmedBillTo,
      customerPhone: trimmedCustomerPhone || undefined,
      visitDate,
      salespersonId: selectedSalesperson.id,
      salespersonName: selectedSalesperson.fullName,
      items,
      totalAmount: totals.totalAmount
    });
    if (!receipt) {
      setIsSaving(false);
      setError('The sales receipt could not be saved. Confirm billing access and try again.');
      return;
    }
    onSaved(receipt, action);
  };
  return <div className="mx-auto max-w-6xl space-y-6 pb-8" data-id="element-1831" data-mp="3m1f8r8epzl4h">
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-5 sm:flex-row sm:items-end sm:justify-between" data-id="element-1832" data-mp="4sa6l6okffnx">
        <div data-id="element-1833" data-mp="3vz3522rz50or">
          <p className="text-sm font-semibold text-pink-600" data-id="element-1834" data-mp="3vzcthlxdezsc">
            Cashflow workspace
          </p>
          <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-950" data-id="element-1835" data-mp="jbtbjdrf5yr7">
            New Sales Receipt
          </h2>
          <p className="mt-1 text-sm text-slate-600" data-id="element-1836" data-mp="9g02w5gtmpep">
            Record a paid cash sale without creating or changing an invoice.
          </p>
        </div>
        <span className="w-fit rounded-md border border-slate-200 bg-white px-3 py-1.5 font-mono text-sm font-semibold text-slate-700" data-id="element-1837" data-mp="eczzlprs6ft5">
          {getNextSalesReceiptNumber()}
        </span>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert" data-id="element-1838" data-mp="4ihvijigci8t">
          {error}
        </div>}

      <Card data-id="element-1839" data-mp="3rrmudz983mbf">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-id="element-1840" data-mp="3rrd1o3wsn121">
          <div className="space-y-4" data-id="element-1841" data-mp="3cx06ou3owdwn">
            <Input label="Bill to" required value={billTo} onChange={event => setBillTo(event.target.value)} placeholder="Customer or organization name" data-id="element-1842" data-mp="3hvjtewq3xtq2" />
            <Input label="Customer Phone" type="tel" value={customerPhone} onChange={event => setCustomerPhone(event.target.value)} placeholder="Optional phone number" data-id="element-1843" data-mp="2tz6qwetqaux9" />
            <Select label="Sales Person" required value={salespersonId} onChange={event => setSalespersonId(event.target.value)} options={[{
            value: '',
            label: '-- Select sales person --'
          }, ...users.map(user => ({
            value: user.id,
            label: `${user.fullName} · ${user.role}`
          }))]} data-id="element-1844" data-mp="2k07fwplhqn73" />
          </div>
          <div className="space-y-4" data-id="element-1845" data-mp="1rd3h52czr53r">
            <Input label="Visit Date" type="date" required value={visitDate} onChange={event => setVisitDate(event.target.value)} data-id="element-1846" data-mp="1rc1stw6ysm43" />
            <div data-id="element-1847" data-mp="bsc940swdbps">
              <p className="text-sm font-medium text-slate-700" data-id="element-1848" data-mp="gti465a15b2h">Number</p>
              <p className="mt-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-sm font-semibold text-slate-700" data-id="element-1849" data-mp="grf8b4m74npu">
                {getNextSalesReceiptNumber()}
              </p>
            </div>
          </div>
        </div>
      </Card>

      <Card noPadding data-id="element-1850" data-mp="ujvgskxv9dy5">
        <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-1851" data-mp="uj3i318z303n">
          <div data-id="element-1852" data-mp="14iubt0pplb2o">
            <h3 className="font-bold text-slate-900" data-id="element-1853" data-mp="14jchsp26b3ns">Cash sale items</h3>
            <p className="mt-0.5 text-sm text-slate-500" data-id="element-1854" data-mp="31bcq41ngwq42">
              Line amounts are pre-tax; a single 5% TOT tax is added to the
              discounted subtotal.
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-1855" data-mp="1c2lr6iaoj2cu">
            <Button size="sm" variant="outline" onClick={addManualItem} data-id="element-1856" data-mp="1c1alue1l5mjm">
              <PlusIcon className="mr-1.5 h-4 w-4" data-id="element-1857" data-mp="174aiwc018zwq" /> Add manual item
            </Button>
            <Button size="sm" onClick={() => setShowItemSearch(true)} data-id="element-1858" data-mp="1lsyak76rcphe">
              <SearchIcon className="mr-1.5 h-4 w-4" data-id="element-1859" data-mp="1lu9cs2kk1p6e" /> Add from catalogue
            </Button>
          </div>
        </div>

        {showItemSearch && <div className="border-b border-blue-100 bg-blue-50 px-5 py-4" data-id="element-1860" data-mp="t4ixhq76sh8p">
            <label className="sr-only" htmlFor="sales-receipt-item-search" data-id="element-1861" data-mp="o569h24kx44j">
              Search service catalogue
            </label>
            <div className="relative max-w-xl" data-id="element-1862" data-mp="133i8vgjc6qze">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" data-id="element-1863" data-mp="y33yvsy6d6yq" />
              <input id="sales-receipt-item-search" autoFocus value={itemSearchQuery} onChange={event => setItemSearchQuery(event.target.value)} placeholder="Search the service catalogue..." className="w-full rounded-md border border-blue-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200" data-id="element-1864" data-mp="29hzoub29uuul" />
            </div>
            <ul className="mt-2 max-h-56 max-w-xl overflow-y-auto rounded-md border border-slate-200 bg-white shadow-sm" aria-label="Available services" data-id="element-1865" data-mp="2c48pflmjncou">
              {filteredItems.length ? filteredItems.map(item => <li key={item.id} data-id="element-1866" data-mp="2qxlpywmrixbr">
                    <button type="button" onClick={() => addCatalogueItem(item)} className="flex w-full items-center justify-between gap-4 border-b border-slate-100 px-3 py-2.5 text-left last:border-0 hover:bg-slate-50 focus:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500" data-id="element-1867" data-mp="2v0rspuao26dh">
                      <span data-id="element-1868" data-mp="37ouvngtwjtqx">
                        <span className="block text-sm font-semibold text-slate-900" data-id="element-1869" data-mp="37o38muedf4c9">
                          {item.serviceName}
                        </span>
                        <span className="block text-xs text-slate-500" data-id="element-1870" data-mp="2xqovp4rnoxop">
                          {item.itemCode} · {item.category}
                        </span>
                      </span>
                      <span className="shrink-0 text-sm font-semibold text-slate-700" data-id="element-1871" data-mp="2oqa6vvfhh0gg">
                        {formatCurrency(item.unitPrice)}
                      </span>
                    </button>
                  </li>) : <li className="px-3 py-4 text-center text-sm text-slate-500" data-id="element-1872" data-mp="2f0z0igjze9w9">
                  No matching services found.
                </li>}
            </ul>
            <button type="button" onClick={() => {
          setShowItemSearch(false);
          setItemSearchQuery('');
        }} className="mt-3 text-sm font-semibold text-blue-700 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1873" data-mp="251860evf7uw6">
              Close search
            </button>
          </div>}

        <div className="overflow-x-auto" data-id="element-1874" data-mp="1qej7af8ztlqu">
          <table className="min-w-[780px] w-full divide-y divide-slate-200" data-id="element-1875" data-mp="1qhovls9ssjli">
            <thead className="bg-slate-50" data-id="element-1876" data-mp="25babd1tm3lms">
              <tr data-id="element-1877" data-mp="20e0bstcidtl8">
                {['No.', 'Item & Description', 'Qty', 'Rate', 'Amount', ''].map(heading => <th key={heading || 'actions'} scope="col" className={`px-5 py-3 text-xs font-bold uppercase tracking-wide text-slate-500 ${heading === 'Item & Description' || heading === 'No.' ? 'text-left' : 'text-right'}`} data-id="element-1878" data-mp="2e3vg3e0errsm">
                      {heading}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white" data-id="element-1879" data-mp="1ksf6ll6q7jnk">
              {items.length ? items.map((item, index) => <tr key={item.id} data-id="element-1880" data-mp="1pqqgfvx0tu5s" data-mpi={index}>
                    <td className="px-5 py-3 text-sm text-slate-500" data-id="element-1881" data-mp="1ppp4zqc3f5nj">
                      {index + 1}
                    </td>
                    <td className="px-5 py-3" data-id="element-1882" data-mp="1zhuvevmp5f0m">
                      <label className="sr-only" htmlFor={`sales-item-${item.id}`} data-id="element-1883" data-mp="1zgtn2yx2f6ml">
                        Item and description for line {index + 1}
                      </label>
                      <input id={`sales-item-${item.id}`} value={item.itemDescription} onChange={event => updateItem(item.id, 'itemDescription', event.target.value)} placeholder="Item and description" className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-id="element-1884" data-mp="2dhmgfgbmrvo4" />
                    </td>
                    <td className="px-4 py-3" data-id="element-1885" data-mp="uhdgavwfdq75">
                      <label className="sr-only" htmlFor={`sales-quantity-${item.id}`} data-id="element-1886" data-mp="ugc0yf8dmhjm">
                        Quantity for{' '}
                        {item.itemDescription || `line ${index + 1}`}
                      </label>
                      <input id={`sales-quantity-${item.id}`} type="number" min="1" step="1" value={item.quantity} onChange={event => updateItem(item.id, 'quantity', event.target.valueAsNumber)} className="w-20 rounded border border-slate-300 px-2 py-1.5 text-right text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-id="element-1887" data-mp="gkzy1kk2hkrm" />
                    </td>
                    <td className="px-4 py-3" data-id="element-1888" data-mp="1gq0q5zttfft4">
                      <label className="sr-only" htmlFor={`sales-rate-${item.id}`} data-id="element-1889" data-mp="1gnobbvjctlka">
                        Rate for {item.itemDescription || `line ${index + 1}`}
                      </label>
                      <input id={`sales-rate-${item.id}`} type="number" min="0" step="0.01" value={item.rate} onChange={event => updateItem(item.id, 'rate', event.target.valueAsNumber)} className="w-28 rounded border border-slate-300 px-2 py-1.5 text-right text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-id="element-1890" data-mp="3nf5ip6peannk" />
                    </td>
                    <td className="px-5 py-3 text-right text-sm font-semibold text-slate-900" data-id="element-1891" data-mp="2wajfgsv3v9nz">
                      {formatCurrency(item.quantity * item.rate)}
                    </td>
                    <td className="px-3 py-3 text-right" data-id="element-1892" data-mp="365bhq9zmukp3">
                      <button type="button" onClick={() => setItems(current => current.filter(entry => entry.id !== item.id))} className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500" aria-label={`Remove ${item.itemDescription || `line ${index + 1}`}`} data-id="element-1893" data-mp="2igs5jjdgpnrq">
                        <Trash2Icon className="h-4 w-4" data-id="element-1894" data-mp="3gzvio19p1536" />
                      </button>
                    </td>
                  </tr>) : <tr data-id="element-1895" data-mp="2o9vantjwbbw3">
                  <td colSpan={6} className="px-5 py-12 text-center" data-id="element-1896" data-mp="2o9dhj5sj5dse">
                    <p className="text-sm font-semibold text-slate-700" data-id="element-1897" data-mp="2t75c67kk7e0a">
                      No items added
                    </p>
                    <p className="mt-1 text-sm text-slate-500" data-id="element-1898" data-mp="2y56q6e34on4w">
                      Add a catalogue service or a manual cash-sale item.
                    </p>
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end border-t border-slate-200 bg-slate-50 p-5" data-id="element-1899" data-mp="1xxrx81fb15hl">
          <dl className="w-full max-w-xs space-y-3 rounded-lg border border-slate-200 bg-white p-4 text-sm shadow-sm" data-id="element-1900" data-mp="1t11h5e3i29w6">
            <div className="flex justify-between gap-4" data-id="element-1901" data-mp="1o7p5e4jx5xku">
              <dt className="text-slate-600" data-id="element-1902" data-mp="1jaqd7rjtg0pa">Subtotal</dt>
              <dd className="font-medium text-slate-900" data-id="element-1903" data-mp="1j9x5an94f55k">
                {formatCurrency(totals.subtotal)}
              </dd>
            </div>
            <div className="flex justify-between gap-4" data-id="element-1904" data-mp="1a8z74lld5ruw">
              <dt className="text-slate-600" data-id="element-1905" data-mp="1ablf2mc629dn">TOT Tax (5%)</dt>
              <dd className="font-medium text-slate-900" data-id="element-1906" data-mp="15bgw8pnsd2k9">
                {formatCurrency(totals.totalTax)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-4 border-t border-slate-200 pt-3 text-base font-bold" data-id="element-1907" data-mp="10dp5w02shoi6">
              <dt className="text-slate-950" data-id="element-1908" data-mp="2uplrhcejw05b">Total</dt>
              <dd className="text-blue-700" data-id="element-1909" data-mp="2uq590s0agmit">
                {formatCurrency(totals.totalAmount)}
              </dd>
            </div>
          </dl>
        </div>
      </Card>

      <div className="flex flex-wrap justify-end gap-3" data-id="element-1910" data-mp="28i1fu9dj0vfm">
        <Button variant="outline" onClick={onCancel} disabled={isSaving} data-id="element-1911" data-mp="1uro5k0d5x4n4">
          Cancel
        </Button>
        <Button variant="outline" onClick={() => saveReceipt('print')} disabled={isSaving} data-id="element-1912" data-mp="1yt1dqh3q9jx7">
          <PrinterIcon className="mr-1.5 h-4 w-4" data-id="element-1913" data-mp="1ksgs7d6fha00" />
          {isSaving ? 'Saving…' : 'Save & Print'}
        </Button>
        <Button onClick={() => saveReceipt('download')} disabled={isSaving} data-id="element-1914" data-mp="1pu65z0l1c1rs">
          <DownloadIcon className="mr-1.5 h-4 w-4" data-id="element-1915" data-mp="1btvebvbwij5k" />
          {isSaving ? 'Saving…' : 'Save & Download PDF'}
        </Button>
      </div>
    </div>;
}
function createSalesReceiptItem(input: Pick<SalesReceiptItem, 'itemDescription' | 'rate'> & {
  itemId?: string;
}): SalesReceiptItem {
  return {
    id: `sales-item-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    itemId: input.itemId,
    itemDescription: input.itemDescription,
    quantity: 1,
    rate: input.rate,
    amount: input.rate
  };
}
function finiteNumber(value: number) {
  return Number.isFinite(value) ? value : 0;
}